import { type ReactNode, useCallback, useEffect, useState } from 'react';
import LanguageSwitcher from './LanguageSwitcher';
import Notifications from './Notifications';
import { useT } from '../i18n';
import { fetchSubscription, fetchFeatures, type PlanFeatures, exitImpersonation } from '../services/api';
import { useClienteAtivo } from '../contexts/ClienteAtivoContext';

// Issue #550 (feat/audit-gestao): 'audit' de volta ao menu — agora subitem
// do grupo "Gestao" (exposta aos tenants, AUDIT_ROLES do backend).
export type NavView = 'home' | 'upload' | 'documents' | 'billing' | 'quota' | 'team' | 'admin' | 'settings' | 'audit' | 'clientes' | 'catalogo' | 'request-provider' | 'onboarding' | 'sefaz' | 'sefaz-status' | 'sefaz-dfe' | 'sefaz-certs' | 'login' | 'account' | 'pricing' | 'signup' | 'terms' | 'privacy' | 'cookies' | 'custodia' | 'install' | 'invite' | 'subscription';

// Issue #475: subitens do grupo "Fiscal / SEFAZ" (extensivel — futuras
// tools MCP viram novos subitens, nunca novos itens top-level).
const SEFAZ_SUBVIEWS: NavView[] = ['sefaz-status', 'sefaz', 'sefaz-dfe', 'sefaz-certs'];

// Issue #550: subitens do grupo "Gestao" (ex-"Plataforma") — Configuracoes
// e Auditoria LGPD entram no dropdown junto de Equipe/Clientes/Municipios.
const GESTAO_SUBVIEWS: NavView[] = ['settings', 'audit', 'team', 'clientes', 'catalogo', 'request-provider'];

// Issue #479: views da secao CONTA — saem do header e ganham sidebar lateral.
const CONTA_VIEWS: NavView[] = ['account', 'billing', 'subscription', 'quota'];

// Issue #559 (fix tap/click): o dropdown so abre no `focus` quando o foco
// veio do TECLADO. Em Chrome/Edge, tap/pen e click de mouse disparam `focus`
// ANTES do `click` no mesmo gesto — sem o filtro, `onFocus` abria o menu e
// o `onClick` (toggle) fechava na sequencia (1o tap morto; click morto logo
// apos Esc com o mouse sobre o trigger). `:focus-visible` so casa com foco
// iniciado por teclado (Tab) ou gestos equivalentes, entao o click-toggle
// volta a funcionar em touch/pen/mouse e o Tab continua abrindo (a11y).
// Fallback `true`: browsers sem :focus-visible mantem o comportamento
// anterior (abre em qualquer foco) em vez de quebrar o handler.
const isKeyboardFocus = (target: EventTarget): boolean => {
  try {
    return target instanceof Element && target.matches(':focus-visible');
  } catch {
    return true;
  }
};

// Issue #245: Roles que podem ver cada item de navegacao.
const UPLOAD_ROLES = ['super_user', 'admin', 'member'];
// Issue #550 (feat/audit-gestao): AUDIT_ROLES restaurada — auditoria volta
// ao menu como subitem de "Gestao", agora visivel para os tenants.
// Espelha AUDIT_ROLES do backend (backend/app/core/roles.py).
const AUDIT_ROLES = ['super_user', 'parceiro', 'admin', 'owner', 'auditor'];
const BILLING_ROLES = ['super_user', 'admin'];
// Issue #328/#332: Team visivel para admin/super_user; Admin apenas super_user.
const TEAM_ROLES = ['super_user', 'admin'];
const ADMIN_ROLES = ['super_user'];
// S10-T01: Configuracoes do tenant visivel para MANAGE_ROLES
// (admin/owner/parceiro/super_user).
const SETTINGS_ROLES = ['super_user', 'parceiro', 'admin', 'owner'];
// Issue #329: Clientes visivel para admin/super_user/contador.
const CLIENTES_ROLES = ['super_user', 'admin', 'contador'];
// Catalogo de Municipios (visao completa: stats/export/import/historico) —
// decisao de produto: apenas super_user. Parceiro moderou a fila de
// sugestoes na nova pagina "Solicitar provedor".
const CATALOGO_ROLES = ['super_user'];
// "Solicitar provedor" — pagina para usuarios de org (provedores
// suportados + formulario de sugestao + minhas solicitacoes; para
// parceiro/super_user inclui a fila de moderacao). Todas as roles exceto
// super_user (que ja tem o catalogo completo).
const REQUEST_PROVIDER_ROLES = [
  'parceiro',
  'admin',
  'owner',
  'member',
  'contador',
  'revisor',
  'financeiro',
  'operador',
  'auditor',
];

interface LayoutProps {
  children: ReactNode;
  currentView?: NavView;
  onNavigate?: (view: NavView) => void;
  user?: {
    email: string;
    role?: string;
    // Issue #330: contexto de impersonation (super_user "acessar como"
    // tenant) — mesmo shape de UserMe em services/api.ts.
    impersonating_org_id?: string | null;
    impersonating_org_name?: string | null;
  } | null;
  onLogout?: () => void;
}

function Layout({ children, currentView, onNavigate, user, onLogout }: LayoutProps) {
  const t = useT();
  const role = user?.role ?? 'member';
  // Issue #329: seletor de cliente ativo do contador.
  const { clienteAtivoId, clienteAtivo, clientes, setClienteAtivoId } = useClienteAtivo();

  // Issue #249: badge do plano atual no menu.
  // Busca /api/billing/subscription ao carregar; se status="active" e plano
  // pago -> badge colorido com o nome do plano; caso contrario "Free".
  // Issue #593: reconhece as keys do contrato #592 (single/team/enterprise)
  // alem de 'pro' legado.
  const [planBadge, setPlanBadge] = useState<string>('free');

  // Issue #248: Gating de features por plano.
  // Busca /api/billing/features ao carregar (quando user logado) e armazena
  // o mapa de features para ocultar/exibir itens de navegacao e acoes.
  const [features, setFeatures] = useState<PlanFeatures | null>(null);

  // Issue #248: Banner de upgrade exibido quando a API retorna 402.
  const [upgradeMsg, setUpgradeMsg] = useState<string | null>(null);
  // Issue #261: Banner de sessao expirada exibida quando a API retorna 401.
  const [sessionExpired, setSessionExpired] = useState(false);
  // Issue #330: banner de impersonation (super_user "acessando como" tenant).
  const [exitingImpersonation, setExitingImpersonation] = useState(false);
  // Issue #475: dropdown do grupo "Fiscal / SEFAZ" (nav desktop).
  const [sefazOpen, setSefazOpen] = useState(false);
  // Issue #550: dropdown do grupo "Gestao" (nav desktop).
  const [gestaoOpen, setGestaoOpen] = useState(false);

  // Fecha os dropdowns ao clicar fora.
  // Issue #549: tambem fecha com Esc (acessibilidade — padrao de dropdown).
  // Issue #550: o mesmo efeito cobre o dropdown "Gestao".
  useEffect(() => {
    if (!sefazOpen && !gestaoOpen) return;
    const close = () => {
      setSefazOpen(false);
      setGestaoOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [sefazOpen, gestaoOpen]);

  const handleExitImpersonation = useCallback(async () => {
    setExitingImpersonation(true);
    try {
      await exitImpersonation();
      // Recarrega para atualizar o estado do usuario.
      window.location.reload();
    } catch {
      // Mesmo em erro, recarrega para limpar o estado.
      window.location.reload();
    } finally {
      setExitingImpersonation(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setPlanBadge('free');
      setFeatures(null);
      return;
    }
    let cancelled = false;
    fetchSubscription()
      .then((sub) => {
        if (cancelled) return;
        // Issue #593: qualquer plano pago ativo ganha badge proprio
        // (single/team/enterprise; 'pro' cobre assinaturas legadas).
        if (sub.status === 'active' && sub.plan && sub.plan !== 'free') {
          setPlanBadge(sub.plan);
        } else {
          setPlanBadge('free');
        }
      })
      .catch(() => {
        if (!cancelled) setPlanBadge('free');
      });
    // Issue #248: busca o mapa de features para gating de UI.
    fetchFeatures()
      .then((resp) => {
        if (cancelled) return;
        setFeatures(resp.features);
      })
      .catch(() => {
        if (!cancelled) setFeatures(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Issue #248: escuta evento de upgrade (402) disparado pelo cliente HTTP.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Issue #593/M2 (review #595): mensagem generica — nao existe mais
      // um plano unico 'Pro'; o plano alvo depende do que a API oferece.
      setUpgradeMsg(detail?.message || 'Recurso premium. Faca upgrade de plano.');
    };
    window.addEventListener('auranota:upgrade-required', handler as EventListener);
    return () => window.removeEventListener('auranota:upgrade-required', handler as EventListener);
  }, []);
  // Issue #261: escuta evento de sessao expirada (401) disparado pelo cliente HTTP.
  // Exibe um banner amigavel (i18n) e leva o usuario a tela de login.
  useEffect(() => {
    const handler = () => {
      setSessionExpired(true);
      onNavigate?.('login');
    };
    window.addEventListener('auranota:session-expired', handler as EventListener);
    return () => window.removeEventListener('auranota:session-expired', handler as EventListener);
  }, [onNavigate]);

  // Issue #369: limpa o banner de sessao expirada assim que o usuario volta a
  // estar autenticado (login bem-sucedido). Antes o banner so era limpo pelo
  // handler do botao "Entrar novamente" ou pelo (x), entao quem fazia login
  // direto pelo formulario continuava vendo "Sua sessao expirou" apos logar.
  // Como `user` so e truthy quando ha sessao valida, limpar aqui e seguro e
  // nao afeta o fluxo de logout (que seta user=null).
  useEffect(() => {
    if (user) setSessionExpired(false);
  }, [user]);

  // Issue #245/#248: navegacao condicional por role E por feature do plano.
  // Menu reorganizado: itens diferentes para logado vs deslogado.
  // - Deslogado: Home, Pricing
  // - Logado: Home, Upload, Documents, Fiscal/SEFAZ, Team, Clientes, Admin
  // Issue #549: 'onboarding' e 'install' sairam do menu (views continuam).
  // Issue #550 (feat/audit-gestao): Configuracoes saiu do top-level e
  // Auditoria LGPD voltou — ambas subitens do grupo "Gestao".
  // Issue #479: Billing, Assinatura (subscription), Quota e Minha Conta sairam
  // do header — agora vivem na sidebar lateral da secao CONTA.
  //   (Pricing/Login/Signup ocultos para logados)
  // Issue #475: subitens do grupo "Fiscal / SEFAZ".
  const sefazItems: { view: NavView; label: string }[] = [
    { view: 'sefaz-status', label: t('sefaz.nav.status') },
    { view: 'sefaz', label: t('sefaz.nav.consulta') },
    { view: 'sefaz-dfe', label: t('sefaz.nav.dfe') },
    { view: 'sefaz-certs', label: t('sefaz.nav.certs') },
  ];

  // Issue #550: subitens do grupo "Gestao" (ex-"Plataforma") — mesmos
  // filtros de role dos antigos itens top-level. Configuracoes (SETTINGS_
  // ROLES) e Auditoria LGPD (AUDIT_ROLES) entram como primeiros subitens.
  const gestaoItems: { view: NavView; label: string; roles?: string[] }[] = [
    { view: 'settings', label: t('layout.nav.settings'), roles: SETTINGS_ROLES },
    { view: 'audit', label: t('layout.nav.audit'), roles: AUDIT_ROLES },
    { view: 'team', label: t('layout.nav.team'), roles: TEAM_ROLES },
    { view: 'clientes', label: t('layout.nav.clients'), roles: CLIENTES_ROLES },
    // Catalogo completo: apenas super_user (decisao de produto). Para
    // usuarios de org, a pagina "Solicitar provedor" substitui o catalogo.
    { view: 'catalogo', label: t('layout.nav.catalogo'), roles: CATALOGO_ROLES },
    { view: 'request-provider', label: t('layout.nav.requestProvider'), roles: REQUEST_PROVIDER_ROLES },
  ];
  // O dropdown so aparece se >=1 subitem for visivel para a role.
  const visibleGestaoItems = gestaoItems.filter(
    (item) => !item.roles || item.roles.includes(role),
  );

  // Issue #479: itens da sidebar lateral da secao CONTA.
  // Mesmo gating por role que existia no header (billing/subscription so para
  // admin/super_user). Minha Conta e Cota visiveis para todo usuario logado.
  const contaItems: { view: NavView; label: string; roles?: string[] }[] = [
    { view: 'account', label: t('layout.nav.myAccount') },
    { view: 'billing', label: t('layout.nav.billing'), roles: BILLING_ROLES },
    { view: 'subscription', label: t('layout.nav.subscription'), roles: BILLING_ROLES },
    { view: 'quota', label: t('layout.nav.quota') },
  ];
  const contaActive = !!user && CONTA_VIEWS.includes(currentView as NavView);
  const visibleContaItems = contaItems.filter((item) => !item.roles || item.roles.includes(role));

  // Issue #549: 'onboarding' (fluxo one-shot) e 'install' (coberto pelo FAB
  // InstallFloatButton) sairam do menu — as VIEWS continuam acessiveis
  // (guards e rotas internas em App.tsx).
  const navItems: { view: NavView; label: string; requiresAuth: boolean; roles?: string[]; feature?: keyof PlanFeatures; group?: 'sefaz' | 'gestao' }[] = user
    ? [
        // LOGADO
        { view: 'home', label: t('layout.nav.home'), requiresAuth: false },
        { view: 'upload', label: t('layout.nav.upload'), requiresAuth: true, roles: UPLOAD_ROLES },
        { view: 'documents', label: t('layout.nav.documents'), requiresAuth: true },
        // Issue #550: Configuracoes/Auditoria/Equipe/Clientes/Municipios
        // consolidados no dropdown "Gestao" (filtros de role nos subitens).
        { view: 'settings', label: t('layout.nav.gestao'), requiresAuth: true, group: 'gestao' },
        { view: 'sefaz', label: t('layout.nav.sefazGroup'), requiresAuth: true, group: 'sefaz' },
        { view: 'admin', label: t('layout.nav.admin'), requiresAuth: true, roles: ADMIN_ROLES },
      ]
    : [
        // DESLOGADO
        { view: 'home', label: t('layout.nav.home'), requiresAuth: false },
        { view: 'pricing', label: t('layout.nav.pricing'), requiresAuth: false },
      ];

  // Issue #249/#593: renderiza o badge do plano (Free=cinza; qualquer plano
  // pago — single/team/pro/enterprise — em azul com o rotulo do plano).
  const PLAN_BADGE_LABEL: Record<string, string> = {
    free: 'Free',
    pro: 'Pro',
    single: 'Single',
    team: 'Team',
    enterprise: 'Enterprise',
  };
  const PlanBadge = () => (
    <span
      className={`ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold ${
        planBadge === 'free'
          ? 'bg-gray-200 text-gray-600'
          : 'bg-blue-100 text-blue-700'
      }`}
    >
      {PLAN_BADGE_LABEL[planBadge] ?? planBadge}
    </span>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-brand-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          {/* Issue #549: logo clicavel como <button> (acessibilidade). */}
          <button
            type="button"
            className="cursor-pointer text-lg font-semibold text-brand-700"
            onClick={() => onNavigate?.('home')}
          >
            {t('layout.brand')}
          </button>
          {/* Issue #549: breakpoint sm->lg — a nav completa (ate 12 itens
              para super_user) estourava entre 640px e ~1150px. */}
          <nav className="hidden gap-6 text-sm font-medium text-brand-600 lg:flex">
            {navItems.map((item) => {
              if (item.requiresAuth && !user) return null;
              if (item.roles && !item.roles.includes(role)) return null;
              // Issue #248: gating por feature do plano.
              if (item.feature && features && !features[item.feature]) return null;
              // Issue #475: grupo "Fiscal / SEFAZ" com dropdown de subitens.
              if (item.group === 'sefaz') {
                const groupActive = SEFAZ_SUBVIEWS.includes(currentView as NavView);
                return (
                  // Issue #559: no desktop o dropdown abre no hover do
                  // container (trigger+menu) — o wrapper pt-1 cobre o gap
                  // visual entre trigger e menu, evitando flicker ao mover
                  // o mouse. Click no trigger continua alternando, Esc e
                  // click-outside fecham. onFocus/onBlur mantêm a
                  // navegação por teclado funcionando.
                  // pointerType 'mouse': em telas de toque o tap dispara
                  // pointerenter+click — sem o filtro o menu abriria e
                  // fecharia no mesmo toque.
                  <span
                    key="sefaz-group"
                    className="relative"
                    onPointerEnter={(e) => {
                      if (e.pointerType !== 'mouse') return;
                      setSefazOpen(true);
                      setGestaoOpen(false);
                    }}
                    onPointerLeave={(e) => {
                      if (e.pointerType !== 'mouse') return;
                      setSefazOpen(false);
                    }}
                    onFocus={(e) => {
                      // #559 fix: so abre em foco de teclado (:focus-visible)
                      // — foco por tap/click nao abre (o click faz o toggle).
                      if (!isKeyboardFocus(e.target)) return;
                      setSefazOpen(true);
                      setGestaoOpen(false);
                    }}
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                        setSefazOpen(false);
                      }
                    }}
                  >
                    {/* Issue #549: trigger do dropdown como <button> com
                        aria-expanded/aria-haspopup (acessibilidade). */}
                    <button
                      type="button"
                      aria-expanded={sefazOpen}
                      aria-haspopup="true"
                      className={`cursor-pointer hover:text-brand-900 ${groupActive ? 'text-brand-900 font-semibold border-b-2 border-brand-700' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setSefazOpen((o) => !o); setGestaoOpen(false); }}
                    >
                      {item.label} ▾
                    </button>
                    {sefazOpen && (
                      <div className="absolute left-0 top-full z-50 min-w-[200px] pt-1">
                        <div className="rounded-md border border-brand-200 bg-white py-1 shadow-lg">
                          {sefazItems.map((sub) => (
                            <button
                              type="button"
                              key={sub.view}
                              className={`block w-full cursor-pointer px-4 py-2 text-left hover:bg-brand-50 ${currentView === sub.view ? 'font-semibold text-brand-900' : ''}`}
                              onClick={() => { setSefazOpen(false); onNavigate?.(sub.view); }}
                            >
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </span>
                );
              }
              // Issue #550: grupo "Gestao" com dropdown de subitens —
              // mesmo padrao do grupo SEFAZ. So rende se >=1 subitem for
              // visivel para a role (ex.: member ve apenas Municipios).
              if (item.group === 'gestao') {
                if (visibleGestaoItems.length === 0) return null;
                const groupActive = GESTAO_SUBVIEWS.includes(currentView as NavView);
                return (
                  // Issue #559: mesmo padrão de hover do grupo SEFAZ —
                  // abre no pointerenter (mouse) do container, fecha no
                  // pointerleave, click/Esc/click-outside e teclado
                  // preservados.
                  <span
                    key="gestao-group"
                    className="relative"
                    onPointerEnter={(e) => {
                      if (e.pointerType !== 'mouse') return;
                      setGestaoOpen(true);
                      setSefazOpen(false);
                    }}
                    onPointerLeave={(e) => {
                      if (e.pointerType !== 'mouse') return;
                      setGestaoOpen(false);
                    }}
                    onFocus={(e) => {
                      // #559 fix: so abre em foco de teclado (:focus-visible)
                      // — foco por tap/click nao abre (o click faz o toggle).
                      if (!isKeyboardFocus(e.target)) return;
                      setGestaoOpen(true);
                      setSefazOpen(false);
                    }}
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                        setGestaoOpen(false);
                      }
                    }}
                  >
                    <button
                      type="button"
                      aria-expanded={gestaoOpen}
                      aria-haspopup="true"
                      className={`cursor-pointer hover:text-brand-900 ${groupActive ? 'text-brand-900 font-semibold border-b-2 border-brand-700' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setGestaoOpen((o) => !o); setSefazOpen(false); }}
                    >
                      {item.label} ▾
                    </button>
                    {gestaoOpen && (
                      <div className="absolute left-0 top-full z-50 min-w-[200px] pt-1">
                        <div className="rounded-md border border-brand-200 bg-white py-1 shadow-lg">
                          {visibleGestaoItems.map((sub) => (
                            <button
                              type="button"
                              key={sub.view}
                              data-tour={`nav-${sub.view}`}
                              className={`block w-full cursor-pointer px-4 py-2 text-left hover:bg-brand-50 ${currentView === sub.view ? 'font-semibold text-brand-900' : ''}`}
                              onClick={() => { setGestaoOpen(false); onNavigate?.(sub.view); }}
                            >
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </span>
                );
              }
              const active = currentView === item.view;
              return (
                // Issue #549: item de nav como <button> com aria-current
                // no ativo (acessibilidade — navegavel por teclado/leitor).
                <button
                  type="button"
                  key={item.view}
                  // S10-T07: alvo do tour guiado do onboarding (spotlight).
                  data-tour={`nav-${item.view}`}
                  aria-current={active ? 'page' : undefined}
                  className={`cursor-pointer hover:text-brand-900 ${active ? 'text-brand-900 font-semibold border-b-2 border-brand-700' : ''}`}
                  onClick={() => onNavigate?.(item.view)}
                >
                  {item.label}
                  {item.view === 'billing' && <PlanBadge />}
                </button>
              );
            })}
          </nav>
          <div className="flex items-center gap-3 text-sm">
            {/* Issue #329: seletor de cliente ativo do contador. */}
            {user && CLIENTES_ROLES.includes(role) && (
              <select
                value={clienteAtivoId ?? ''}
                onChange={(e) => setClienteAtivoId(e.target.value || null)}
                className="max-w-[180px] truncate rounded-md border border-brand-300 bg-white px-2 py-1 text-xs text-brand-700"
                title={clienteAtivo ? clienteAtivo.nome : t('layout.clientSelector.all')}
              >
                <option value="">{t('layout.clientSelector.all')}</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            )}
            {user && <Notifications />}
            {user ? (
              <>
                <span className="hidden text-brand-600 sm:inline">{user.email}</span>
                <button
                  type="button"
                  onClick={() => onNavigate?.('account')}
                  className={`text-brand-700 underline ${contaActive ? 'font-semibold' : ''}`}
                >
                  {t('layout.account')}
                </button>
                <button
                  type="button"
                  onClick={onLogout}
                  className="text-brand-700 underline"
                >
                  {t('layout.logout')}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate?.('login')}
                className="rounded-md bg-brand-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-800"
              >
                {t('layout.login')}
              </button>
            )}
          </div>
        </div>
        {/* Nav mobile — Issue #549: breakpoint sm->lg (cobre a faixa
            640–1150px onde a nav desktop estourava) e itens como <button>
            com aria-current (acessibilidade). */}
        <nav className="flex gap-4 overflow-x-auto border-t border-brand-100 px-4 py-2 text-sm text-brand-600 lg:hidden">
          {navItems.map((item) => {
            if (item.requiresAuth && !user) return null;
            if (item.roles && !item.roles.includes(role)) return null;
            // Issue #248: gating por feature do plano.
            if (item.feature && features && !features[item.feature]) return null;
            // Issue #475: no mobile o grupo SEFAZ rende os subitens
            // achatados (a nav ja e scrollavel horizontalmente).
            if (item.group === 'sefaz') {
              return sefazItems.map((sub) => (
                <button
                  type="button"
                  key={sub.view}
                  aria-current={currentView === sub.view ? 'page' : undefined}
                  className={`whitespace-nowrap cursor-pointer ${currentView === sub.view ? 'font-semibold text-brand-900' : ''}`}
                  onClick={() => onNavigate?.(sub.view)}
                >
                  {sub.label}
                </button>
              ));
            }
            // Issue #550: no mobile o grupo Gestao rende os subitens
            // achatados — mesmo padrao do SEFAZ mobile.
            if (item.group === 'gestao') {
              return visibleGestaoItems.map((sub) => (
                <button
                  type="button"
                  key={sub.view}
                  data-tour={`nav-${sub.view}`}
                  aria-current={currentView === sub.view ? 'page' : undefined}
                  className={`whitespace-nowrap cursor-pointer ${currentView === sub.view ? 'font-semibold text-brand-900' : ''}`}
                  onClick={() => onNavigate?.(sub.view)}
                >
                  {sub.label}
                </button>
              ));
            }
            const active = currentView === item.view;
            return (
              <button
                type="button"
                key={item.view}
                data-tour={`nav-${item.view}`}
                aria-current={active ? 'page' : undefined}
                className={`whitespace-nowrap cursor-pointer ${active ? 'font-semibold text-brand-900' : ''}`}
                onClick={() => onNavigate?.(item.view)}
              >
                {item.label}
                {item.view === 'billing' && <PlanBadge />}
              </button>
            );
          })}
        </nav>
      </header>

      {/* Issue #248: Banner de upgrade exibido quando a API retorna 402. */}
      {upgradeMsg && (
        <div className="mx-auto max-w-6xl px-4 pt-4">
          <div className="flex items-center justify-between gap-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <span>{upgradeMsg}</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => onNavigate?.('billing')}
                className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Fazer upgrade
              </button>
              <button
                type="button"
                onClick={() => setUpgradeMsg(null)}
                className="text-amber-600 hover:text-amber-800"
                aria-label="Fechar"
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      )}

            {/* Issue #261: Banner de sessao expirada exibido quando a API retorna 401. */}
      {sessionExpired && (
        <div className="mx-auto max-w-6xl px-4 pt-4">
          <div className="flex items-center justify-between gap-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            <span>{t('session.expired')}</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => { setSessionExpired(false); onNavigate?.('login'); }}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
              >
                {t('session.loginAgain')}
              </button>
              <button
                type="button"
                onClick={() => setSessionExpired(false)}
                className="text-red-600 hover:text-red-800"
                aria-label="Fechar"
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Issue #330: Banner de impersonation (super_user "acessando como" tenant).
          Issue #549: virou faixa full-width sticky top-0 — estado persistente
          de sessao (diferente dos banners transientes de upgrade/401), deve
          ficar sempre visivel durante o scroll. */}
      {user?.impersonating_org_id && (
        <div className="sticky top-0 z-40 border-b border-purple-300 bg-purple-50 text-sm text-purple-800">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
            <span>
              {t('impersonation.banner', { name: user.impersonating_org_name ?? user.impersonating_org_id })}
            </span>
            <button
              type="button"
              onClick={handleExitImpersonation}
              disabled={exitingImpersonation}
              className="rounded-md bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {exitingImpersonation ? t('impersonation.exiting') : t('impersonation.exit')}
            </button>
          </div>
        </div>
      )}

<main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        {contaActive ? (
          // Issue #479: area CONTA com sidebar lateral. No mobile a sidebar
          // vira uma linha horizontal scrollavel — mesmo padrao da nav mobile
          // do header (overflow-x-auto), sem hamburger.
          <div className="flex flex-col gap-6 sm:flex-row">
            <aside className="sm:w-48 sm:shrink-0">
              <div className="mb-2 hidden text-xs font-semibold uppercase tracking-wide text-brand-500 sm:block">
                {t('layout.account')}
              </div>
              <nav className="flex gap-4 overflow-x-auto text-sm text-brand-600 sm:flex-col sm:gap-1">
                {/* Issue #549: itens da sidebar CONTA como <button> com
                    aria-current (mesma correcao de acessibilidade da nav). */}
                {visibleContaItems.map((item) => (
                  <button
                    type="button"
                    key={item.view}
                    aria-current={currentView === item.view ? 'page' : undefined}
                    className={`whitespace-nowrap cursor-pointer text-left sm:rounded-md sm:px-3 sm:py-2 sm:hover:bg-brand-50 ${
                      currentView === item.view ? 'font-semibold text-brand-900 sm:bg-brand-100' : ''
                    }`}
                    onClick={() => onNavigate?.(item.view)}
                  >
                    {item.label}
                    {item.view === 'billing' && <PlanBadge />}
                  </button>
                ))}
              </nav>
            </aside>
            <div className="min-w-0 flex-1">{children}</div>
          </div>
        ) : (
          children
        )}
      </main>

      <footer className="border-t border-brand-200 bg-white py-4 text-sm text-brand-500">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 sm:flex-row">
          <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-0">
            <div className="mb-0">
              <button
                type="button"
                onClick={() => onNavigate?.('terms')}
                className="mx-2 hover:text-brand-700"
              >
                {t('layout.footer.terms')}
              </button>
              &middot;
              <button
                type="button"
                onClick={() => onNavigate?.('privacy')}
                className="mx-2 hover:text-brand-700"
              >
                {t('layout.footer.privacy')}
              </button>
              &middot;
              <button
                type="button"
                onClick={() => onNavigate?.('cookies')}
                className="mx-2 hover:text-brand-700"
              >
                {t('layout.footer.cookies')}
              </button>
            </div>
            <div className="sm:ml-4">{t('layout.footer.tagline')}</div>
          </div>
          {/* Issue #351: seletor de idioma movido do header para o footer. */}
          <LanguageSwitcher />
        </div>
      </footer>
    </div>
  );
}

export default Layout;
